import dayjs from 'dayjs';
import { z } from 'zod';

import {
  DynamicArtifactTemplate,
  DynamicSummary,
  TextFieldBlock,
} from '@eluve/llm-outputs';
import {
  convertMarkdownToPlainText,
  convertMarkdownToPlainTextRecursively,
  humanReadableDateAndTimeWithDashes,
  splitFullName,
} from '@eluve/utils';

import {
  AppointmentModel,
  ExternalOrganizationModel,
} from '../../models/appointment';
import { ChartType } from '../../models/chart';
import { PatientType } from '../../models/patient';
import { UserType } from '../../models/user';
import {
  BillingCode,
  NormalizedChartResult,
  PatientEhrUrlArgs,
  VendorDataMapper,
  VendorSyncConfig,
} from '../vendor-data-mapper';

import { logo } from './logo';
import {
  LatestSoapNoteSchema,
  RemarksJsonSchema,
  SiloamSignedChartFromApiSchema,
  SiloamSignedChartSchema,
  SubjectiveJsonSchema,
  appointmentSchema,
  appointmentsSchema,
  chartSchema,
} from './types';

const userSchema = z
  .object({
    id: z.string(),
    fullName: z.string(),
    specialty: z.string().nullish(),
    doctororganization: z
      .array(
        z.object({
          OrganizationId: z.number().nullish(),
          OrganizationName: z.string().nullish(),
        }),
      )
      .nullish(),
  })
  .passthrough();

export type EnvConfig = {
  web: string;
  api: string;
  sourceId: string;
};

const ENV_CONFIGURATIONS: EnvConfig[] = [
  {
    web: 'dev-genai-soap.siloamhospitals.com',
    api: 'https://siloam-emr-be-uat.siloamhospitals.com:5511',
    sourceId: '3985g01971bf',
  },
  {
    web: 'genai-soap.siloamhospitals.com',
    api: 'https://siloam-emr-be.siloamhospitals.com:5511',
    sourceId: '1985g98092cf',
  },
];

export class SiloamVendorDataMapper implements VendorDataMapper {
  /**
   * Resolves the environment configuration (API host, source ID) based on the web domain.
   * Supports both UAT (dev-genai-soap) and production (genai-soap) environments.
   */
  getEnvConfig = (domain: string) => {
    const envConfig = ENV_CONFIGURATIONS.find(
      (config) => config.web === domain,
    );
    if (!envConfig) {
      throw new Error(`Unsupported SILOAM domain: ${domain}`);
    }
    return envConfig;
  };

  getSourceId = (domain: string) => {
    return this.getEnvConfig(domain).sourceId;
  };

  getApiBaseUrl = (domain: string) => {
    return this.getEnvConfig(domain).api;
  };

  /**
   * Builds the URL for fetching appointment details from Siloam EMR.
   * Used by the extension to retrieve patient and encounter information.
   */
  buildAppointmentDetailsUrl = (domain: string, encounterId: string) => {
    const { api, sourceId } = this.getEnvConfig(domain);
    return `${api}/api/v1/appointments/${sourceId}/${encounterId}`;
  };

  /**
   * Builds the URL for marking an appointment as synced in Siloam EMR.
   * Called after extension-based recordings to confirm the note was synced.
   */
  buildSyncConfirmationUrl = (domain: string, encounterId: string) => {
    const { api, sourceId } = this.getEnvConfig(domain);
    return `${api}/api/v2/soapai/${sourceId}/${encounterId}`;
  };

  /**
   * Builds the URL for generating a magic link to access an encounter.
   * Used for deep-linking users directly into the Siloam EMR encounter page.
   */
  buildMagicLinkUrl = (domain: string, encounterId: string) => {
    const { api, sourceId } = this.getEnvConfig(domain);
    return `${api}/api/v1/magic-link/${sourceId}/${encounterId}`;
  };

  isMockAppointment = (encounterId: string | null) => {
    return encounterId?.startsWith('eluve-test');
  };

  isSoapPage = (url?: string) => {
    return Boolean(url?.toLowerCase().includes('/form/soap/'));
  };

  isAppointmentsPage = (url?: string) => {
    const lower = url?.toLowerCase();
    return Boolean(
      lower?.includes('/form/general/worklistappointment.aspx') &&
        !lower?.includes('nurse'),
    );
  };

  isEluveDomain = (url: string) => {
    return url.includes('eluve.com') || url.includes('eluve.dev');
  };

  convertLlmOutputToMatchingEhrSignedNoteFormat({
    llmOutputContent,
  }: {
    llmOutputContent: unknown;
    template: DynamicArtifactTemplate;
  }) {
    return convertMarkdownToPlainTextRecursively(llmOutputContent) as Record<
      string,
      unknown
    >;
  }

  /**
   * Pulls Siloam's `doctorId` out of a fetched chart payload (the
   * `LatestSoapNote` shape returned by `/latestsoapbypatientid/:id`).
   * Returned as a string so callers can compare against
   * `external_users.external_id`. Returns `null` for any falsy
   * `doctorId` — absent, `null`, or `0` — which collapses the three
   * "can't-attribute" cases callers all treat the same way. Siloam
   * doctor IDs are positive ints in practice so the `0` case is
   * theoretical.
   */
  getChartDoctorExternalId(rawData: unknown): string | null {
    const parsed = LatestSoapNoteSchema.safeParse(rawData);
    if (!parsed.success || !parsed.data.doctorId) return null;
    return String(parsed.data.doctorId);
  }

  convertRawDataToJsonNote({
    rawData,
    template,
  }: {
    rawData: unknown;
    template: DynamicArtifactTemplate;
  }): NormalizedChartResult {
    if (!template) {
      // We'll ignore this for Siloam since it has its own structure
    }

    const toIsoDate = (dateStr: string | undefined): string | undefined => {
      if (!dateStr) return undefined;
      // Append 'Z' if no timezone to parse as UTC
      const normalizedDateStr =
        dateStr.includes('Z') ||
        dateStr.includes('+') ||
        dateStr.includes('-', 10)
          ? dateStr
          : dateStr + 'Z';
      const date = new Date(normalizedDateStr);
      if (isNaN(date.getTime())) return undefined;
      return date.toISOString();
    };

    const parsedResponse = SiloamSignedChartSchema.safeParse(rawData);
    if (parsedResponse.success) {
      const parsed = parsedResponse.data;
      return {
        chart: {
          plan: parsed.soap.Planning,
          objective: parsed.soap.Objective,
          assessment: parsed.soap.Assessment,
          subjective: {
            anamnesis: parsed.soap.SubjectiveAnamnesis,
            chiefComplaint: parsed.soap.SubjectiveChiefComplaint,
          },
        },
        chartDate: undefined,
        externalAppointmentId: parsed.EncounterId,
        externalPatientId: parsed.PatientId,
      };
    }

    const parsedApiResponse = SiloamSignedChartFromApiSchema.safeParse(rawData);
    if (parsedApiResponse.success) {
      const parsed = parsedApiResponse.data;

      let chiefComplaint = '';
      let anamnesis = '';
      let extractedDate: string | undefined;

      if (parsed.subjective_json) {
        const subjectiveParsed = SubjectiveJsonSchema.safeParse(
          JSON.parse(parsed.subjective_json),
        );
        if (subjectiveParsed.success) {
          const items = subjectiveParsed.data;
          chiefComplaint =
            items.find((item) => item.type === 'PATIENT_COMPLAINT')?.remarks ??
            '';
          anamnesis =
            items.find((item) => item.type === 'ANAMNESIS')?.remarks ?? '';
          for (const item of items) {
            const dateStr = item.created_date ?? item.modified_date;
            if (dateStr) {
              extractedDate = toIsoDate(dateStr);
              if (extractedDate) break;
            }
          }
        }
      }

      const parseRemarksJson = (
        jsonString: string | null | undefined,
      ): { remarks: string; date?: string } => {
        if (!jsonString) return { remarks: '' };
        const remarksParsed = RemarksJsonSchema.safeParse(
          JSON.parse(jsonString),
        );
        if (remarksParsed.success) {
          return {
            remarks: remarksParsed.data.remarks ?? '',
            date:
              remarksParsed.data.created_date ??
              remarksParsed.data.modified_date,
          };
        }
        return { remarks: '' };
      };

      const planResult = parseRemarksJson(parsed.planning_json);
      const objectiveResult = parseRemarksJson(parsed.objective_json);
      const assessmentResult = parseRemarksJson(parsed.assessment_json);

      if (!extractedDate) {
        const dateStr =
          objectiveResult.date ?? assessmentResult.date ?? planResult.date;
        extractedDate = toIsoDate(dateStr);
      }

      return {
        chart: {
          plan: planResult.remarks,
          objective: objectiveResult.remarks,
          assessment: assessmentResult.remarks,
          subjective: {
            anamnesis,
            chiefComplaint,
          },
        },
        chartDate: extractedDate,
        externalAppointmentId: parsed.encounterId ?? undefined,
        externalPatientId: parsed.patientId
          ? String(parsed.patientId)
          : undefined,
      };
    }

    throw new Error('Unable to parse Siloam signed chart data');
  }
  convertChartToSummary({
    data,
    template,
  }: {
    data: unknown;
    template: DynamicArtifactTemplate;
  }): {
    summary: DynamicSummary;
    billingCodes: BillingCode[];
  } | null {
    const type = 'DYNAMIC' as const;
    if (!template) {
      throw new Error('Missing template for Siloam chart conversion');
    }

    let parsedResponse = SiloamSignedChartSchema.safeParse(data);
    if (!parsedResponse.success) {
      const apiParsed = SiloamSignedChartFromApiSchema.safeParse(data);
      if (
        !apiParsed.success ||
        !(
          apiParsed.data.subjective_json ||
          apiParsed.data.objective_json ||
          apiParsed.data.assessment_json ||
          apiParsed.data.planning_json
        )
      ) {
        return null;
      }

      let normalizedChart: NormalizedChartResult;
      try {
        normalizedChart = this.convertRawDataToJsonNote({
          rawData: data,
          template,
        });
      } catch {
        return null;
      }

      if (!normalizedChart.externalAppointmentId) return null;

      const subjective =
        typeof normalizedChart.chart['subjective'] === 'object' &&
        normalizedChart.chart['subjective'] !== null
          ? (normalizedChart.chart['subjective'] as Record<string, unknown>)
          : {};

      const soap = {
        SubjectiveChiefComplaint: subjective['chiefComplaint'],
        SubjectiveAnamnesis: subjective['anamnesis'],
        Objective: normalizedChart.chart['objective'],
        Assessment: normalizedChart.chart['assessment'],
        Planning: normalizedChart.chart['plan'],
      };

      if (
        !Object.values(soap).some(
          (value) => typeof value === 'string' && value.trim().length > 0,
        )
      ) {
        return null;
      }

      parsedResponse = SiloamSignedChartSchema.safeParse({
        EncounterId: normalizedChart.externalAppointmentId,
        PatientId: normalizedChart.externalPatientId,
        soap,
      });
    }

    if (!parsedResponse.success) return null;

    const parsed = parsedResponse.data;
    const blocks = Object.entries(parsed.soap).map(([key, value]) => {
      return {
        type: 'text',
        key: key,
        label: key,
        text: convertMarkdownToPlainText((value as string) ?? ''),
      } as TextFieldBlock;
    });
    const assessmentContent =
      blocks.find((b) => b.key === 'Assessment')?.text ?? '';
    const codeRegex = /ICD-10 Code:\s*([A-Z0-9.]+)\s*-\s*([^\n\r\\]+)/g;
    const billingCodes = [];
    let match;
    while ((match = codeRegex.exec(assessmentContent)) !== null) {
      billingCodes.push({
        code: match[1] ?? '',
        description: match[2]?.trim().split('\\n')[0] ?? '',
      });
    }
    return { summary: { type, blocks }, billingCodes };
  }

  /**
   * Siloam's EMR sends appointment times in WIB (Asia/Jakarta, UTC+7) but
   * may include incorrect timezone suffixes (e.g. "Z" on a WIB time).
   * This strips any existing timezone indicator and appends the correct
   * WIB offset so downstream convertTimeToUTC treats the time correctly.
   */
  private static readonly WIB_OFFSET = '+07:00';

  private static normalizeToWIB(dateTime: string): string {
    const normalized =
      dateTime.replace(/([+-]\d{2}:\d{2}|Z)$/, '') + this.WIB_OFFSET;
    // Use dayjs for stricter validation than new Date() which is too
    // permissive in V8 (e.g. treats random strings as valid dates)
    if (dayjs(normalized).isValid()) return normalized;
    return new Date().toISOString();
  }

  getAppointments(data: unknown): AppointmentModel[] {
    const appointments = appointmentsSchema.parse(data);
    return appointments.map((appointment) => {
      const fullName = splitFullName(appointment.patient.fullName);
      const rawStartTime = appointment.startDate ?? appointment.admissionDate;
      const startTime = rawStartTime
        ? SiloamVendorDataMapper.normalizeToWIB(rawStartTime)
        : new Date().toISOString();
      const endTime = appointment.endDate
        ? SiloamVendorDataMapper.normalizeToWIB(appointment.endDate)
        : '';
      return {
        externalId: appointment.id,
        name:
          appointment.name ??
          `New Session ${humanReadableDateAndTimeWithDashes()}`,
        description: '',
        externalPatientId: appointment.patient.id,
        externalPractitionerId: undefined,
        firstName: fullName.firstName,
        lastName: fullName.lastName,
        startTime,
        endTime,
        rawEhrData: appointment as Record<string, unknown>,
        isArchived: false,
        isImportable: true,
        status: undefined,
        sessionType: undefined,
        organization:
          appointment.organizationId != null
            ? {
                organizationId: appointment.organizationId,
                organizationName: appointment.organizationName,
              }
            : null,
      };
    });
  }

  getUser(data: unknown): UserType {
    const user = userSchema.parse(data);
    const email = `${user.id}-eluve@siloamhospitals.com`;
    const fullName = splitFullName(user.fullName);
    const organizations = (user.doctororganization ?? []).flatMap((org) =>
      org.OrganizationId != null
        ? [
            {
              organizationId: org.OrganizationId,
              organizationName: org.OrganizationName,
            },
          ]
        : [],
    );
    return {
      id: user.id,
      email,
      ...fullName,
      specialty: user.specialty ?? undefined,
      organizations,
    };
  }

  getExternalOrganization(rawData: unknown): ExternalOrganizationModel | null {
    const parsed = z
      .object({
        organizationId: z.number().nullish(),
        organizationName: z.string().nullish(),
      })
      .safeParse(rawData);
    if (!parsed.success || parsed.data.organizationId == null) return null;
    return {
      organizationId: parsed.data.organizationId,
      organizationName: parsed.data.organizationName,
    };
  }

  getPatient(data: unknown): PatientType | null {
    const { patient } = appointmentSchema.parse(data);
    const fullName = splitFullName(patient.fullName);
    return {
      externalPatientId: patient.id,
      firstName: fullName.firstName,
      lastName: fullName.lastName,
      dateOfBirth: patient.birthdate ?? null,
      email: null,
      homePhone: null,
      cellPhone: null,
      workPhone: null,
      gender: patient.gender ?? null,
      rawData: patient as Record<string, unknown>,
    };
  }

  getPatientEhrUrl(data: PatientEhrUrlArgs): string {
    return '';
  }

  getLogo(): string {
    return logo;
  }

  getHomeUrl(domain: string): string {
    return 'https://www.siloamhospitals.com';
  }

  getChart(
    data: unknown,
    additionalFields?: Record<string, unknown>,
  ): ChartType {
    const parsed = chartSchema.parse(data);
    return {
      externalPatientId: parsed.PatientId,
      externalChartId: parsed.EncounterId,
      externalAppointmentId: parsed.EncounterId,
      signedAt: null,
      rawData: data as Record<string, unknown>,
    };
  }

  getSyncConfig(): VendorSyncConfig {
    return {
      isImportEnabled: false,
      isSyncEnabled: true,
      canSignNoteInEhr: false,
      canSyncWithManualChartUrl: false,
      shouldDisplayConfirmationBeforeSync: false,
      canSyncNoteToEhr: ({ externalAppointmentId, isApiSyncEnabled }) => {
        if (!externalAppointmentId) {
          return {
            canSync: false,
            reason: 'Selected appointment was not imported from EHR',
          };
        }
        // DOM-based sync injects into the SOAP page's textareas, so it requires
        // the user to be on that page. API-based sync posts to Siloam directly
        // and works from any page, so skip the page gate when it's enabled.
        if (!isApiSyncEnabled && !this.isSoapPage(window.location.href)) {
          return {
            canSync: false,
            reason: 'Please navigate to the SOAP page to sync notes',
          };
        }
        return { canSync: true };
      },
    };
  }

  getVendorSchema = () => {
    const secretSchema = z.object({
      'Auth Token': z.string(),
      'SOAP URL': z
        .string()
        .url()
        .describe('Additional full URL where SOAP note will be synced to'),
    });
    return { secretSchema };
  };
}
